import store from "../store";
import {
  showModal,
  hideModal,
  resetApp,
  updateUserActiveSessionDelta,
} from "../store/actions";
import { clearAllCookies } from "./utility/Utils";
import { clearAuthStorage } from "./axiosInterceptor";
import { invalidateTokenApi } from "../api/SessionServiceManager";
import { clearSessionTokenCache } from "../api/CortexServiceManager";
import { DOCUMENT_EVENTS, IDLE_TIMEOUT_MSG } from "./constants/constants";
import { ROUTES } from "./constants/routesConstants";
import { STORAGE } from "./constants/storageConstants";
import { coordinatedRefresh } from "./refreshCoordinator";
import { cancelTokenRefresh } from "./proactiveTokenRefresh";

let idleTimeout;
let modalTimeout;
let loginCheckInterval;
let sleepDetectionInterval;
let lastCheckTime = Date.now();
// Use absolute timestamp instead of relative time
let sessionValidUntil = null; // Absolute expiry timestamp
// Prevent multiple simultaneous logout attempts (double navigation)
let isLoggingOut = false;
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes idle timeout
const MODAL_WARNING_TIME_MS = 2 * 60 * 1000; // 2 minutes warning before logout
const LOGIN_CHECK_INTERVAL_MS = 5000; // Check every 5 seconds if we need to start idle timer
const SLEEP_DETECTION_INTERVAL_MS = 5000; // Check every 5 seconds
const SLEEP_THRESHOLD_MS = 30000; // If more than 30 seconds passed, assume sleep

// Check if session has expired based on absolute timestamp (race-free, sleep-safe)
const checkSessionExpiry = () => {
  const currentTime = Date.now();

  if (window.location.href.includes(ROUTES.LOGIN)) return;

  // Don't trigger idle timeout for unauthenticated users (e.g. /dashboard visited without login)
  const state = store.getState();
  const activeSession = state?.sessionReducer?.activeSession?.activeSession;
  if (!activeSession) return;

  // Prevent multiple logout attempts (e.g., from visibility + focus events)
  if (isLoggingOut) {
    console.log("[IDLE] Logout already in progress, skipping duplicate check");
    return;
  }

  // Check against absolute timestamp (atomic, no race condition)
  const modalDeadline = sessionValidUntil + MODAL_WARNING_TIME_MS;

  // If current time exceeds absolute deadline (session + modal warning), force logout
  if (currentTime > modalDeadline) {
    isLoggingOut = true; // Set flag before logout
    handleUserResponse(false, activeSession);
    return;
  }

  // If current time exceeds session validity (but not modal deadline), show modal
  if (currentTime > sessionValidUntil) {
    if (!state?.idleTimeModalReducer?.modalVisible) {
      store.dispatch(
        showModal(
          IDLE_TIMEOUT_MSG
        )
      );
      // Set timeout to auto-logout after modal warning time
      modalTimeout = setTimeout(() => {
        const activeSession = store.getState()?.sessionReducer?.activeSession?.activeSession;
        handleUserResponse(false, activeSession);
      }, MODAL_WARNING_TIME_MS);
    }
    return;
  }

  // If we're still within the allowed time, reschedule the check
  const remainingTime = sessionValidUntil - currentTime;
  clearTimeout(idleTimeout);
  idleTimeout = setTimeout(checkSessionExpiry, remainingTime);
};

// Handle visibility change (machine sleep/wake, tab switching)
const handleVisibilityChange = () => {
  if (!document.hidden) {
    // Always check session expiry when page becomes visible (handles sleep/wake)
    checkSessionExpiry();
  }
};

// Handle page focus (window regained focus)
const handlePageFocus = () => {
  // Always check session expiry when window regains focus
  checkSessionExpiry();
};

export const startIdleTimer = () => {
  // Set absolute expiry timestamp
  sessionValidUntil = Date.now() + IDLE_TIMEOUT_MS;
  lastCheckTime = Date.now();
  // Reset logout flag for new session
  isLoggingOut = false;

  // Listen to user activity events
  document.addEventListener(DOCUMENT_EVENTS.MOUSE_MOVE, resetIdleTimer);
  document.addEventListener(DOCUMENT_EVENTS.KEY_PRESS, resetIdleTimer);
  document.addEventListener(DOCUMENT_EVENTS.CLICK, resetIdleTimer);
  document.addEventListener(DOCUMENT_EVENTS.SCROLL, resetIdleTimer);

  // Listen to visibility change (tab switching, window minimize, sleep/wake)
  document.addEventListener(DOCUMENT_EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);

  // Listen to page focus events
  window.addEventListener(DOCUMENT_EVENTS.FOCUS, handlePageFocus);

  // Try to start the timer (will be skipped by resetIdleTimer if on login page)
  resetIdleTimer();

  // Clear any existing intervals before starting new ones
  if (loginCheckInterval) {
    clearInterval(loginCheckInterval);
    loginCheckInterval = null;
  }
  if (sleepDetectionInterval) {
    clearInterval(sleepDetectionInterval);
    sleepDetectionInterval = null;
  }

  const isOnLoginPage = window.location.href.includes(ROUTES.LOGIN);

  // Only start login check interval if we're currently on the login page
  // This avoids unnecessary polling when user is already authenticated
  if (isOnLoginPage) {
    /* 
     * Periodically check if we've navigated away from login and need to start timer
     * This handles cases where user interaction (click/keypress) happens on /login page
     * (where resetIdleTimer is ignored), then programmatic navigation occurs to authenticated page
     * with no subsequent user interaction to trigger resetIdleTimer
     */

    loginCheckInterval = setInterval(() => {
      if (!window.location.href.includes(ROUTES.LOGIN) && !idleTimeout) {
        // Set absolute expiry timestamp
        sessionValidUntil = Date.now() + IDLE_TIMEOUT_MS;
        lastCheckTime = Date.now(); // Reset sleep detection baseline after login
        idleTimeout = setTimeout(checkSessionExpiry, IDLE_TIMEOUT_MS);

        // Start sleep detection now that user is authenticated
        sleepDetectionInterval = setInterval(() => {
          const now = Date.now();
          const timeSinceLastCheck = now - lastCheckTime;

          if (timeSinceLastCheck > SLEEP_THRESHOLD_MS) {
            checkSessionExpiry();
          }

          lastCheckTime = now;
        }, SLEEP_DETECTION_INTERVAL_MS);

        // Clear the login check interval once timer is started
        clearInterval(loginCheckInterval);
        loginCheckInterval = null;
      }
    }, LOGIN_CHECK_INTERVAL_MS); // Check every 5 seconds
  } else {
    // User is already authenticated, start sleep detection immediately
    sleepDetectionInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheckTime;

      // If more time passed than expected, machine likely slept
      if (timeSinceLastCheck > SLEEP_THRESHOLD_MS) {
        checkSessionExpiry();
      }

      lastCheckTime = now;
    }, SLEEP_DETECTION_INTERVAL_MS);
  }
};

// Cleanup function to stop all timers and intervals
export const stopIdleTimer = () => {
  // Clear all timeouts
  clearTimeout(idleTimeout);
  clearTimeout(modalTimeout);
  idleTimeout = null;
  modalTimeout = null;

  // Clear all intervals
  if (loginCheckInterval) {
    clearInterval(loginCheckInterval);
    loginCheckInterval = null;
  }
  if (sleepDetectionInterval) {
    clearInterval(sleepDetectionInterval);
    sleepDetectionInterval = null;
  }

  // Remove event listeners
  document.removeEventListener(DOCUMENT_EVENTS.MOUSE_MOVE, resetIdleTimer);
  document.removeEventListener(DOCUMENT_EVENTS.KEY_PRESS, resetIdleTimer);
  document.removeEventListener(DOCUMENT_EVENTS.CLICK, resetIdleTimer);
  document.removeEventListener(DOCUMENT_EVENTS.SCROLL, resetIdleTimer);
  document.removeEventListener(DOCUMENT_EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);
  window.removeEventListener(DOCUMENT_EVENTS.FOCUS, handlePageFocus);
};

const resetIdleTimer = () => {
  // If modal is visible, user must explicitly respond to it - ignore activity
  if (store.getState()?.idleTimeModalReducer?.modalVisible) {
    return;
  }

  // Always clear existing timers first
  clearTimeout(idleTimeout);
  clearTimeout(modalTimeout);

  // On login page: set timers to null and exit without resetting
  if (window.location.href.includes(ROUTES.LOGIN)) {
    idleTimeout = null;
    modalTimeout = null;
    sessionValidUntil = null;
    // Keep loginCheckInterval running as it will be needed after successful login
    return;
  }

  // Update absolute expiry timestamp (reset session validity)
  sessionValidUntil = Date.now() + IDLE_TIMEOUT_MS;

  // Schedule next check after idle timeout
  idleTimeout = setTimeout(checkSessionExpiry, IDLE_TIMEOUT_MS);
};

export const handleUserResponse = async (shouldStayLoggedIn, activeSession) => {
  store.dispatch(hideModal());

  // Clear login check interval (no longer needed after user responds to modal)
  if (loginCheckInterval) {
    clearInterval(loginCheckInterval);
    loginCheckInterval = null;
  }

  if (shouldStayLoggedIn) {
    // Reset logout flag when user chooses to stay logged in
    isLoggingOut = false;
    try {
      // Read refreshToken from store at call time — NOT from the stale activeSession parameter.
      // If axiosInterceptor already refreshed between modal open and user click, the parameter
      // holds the old (consumed) token. Reading from store gets the current token. (CWE-362 fix)
      const currentRefreshToken = store.getState()?.sessionReducer?.activeSession?.activeSession?.refreshToken;

      if (currentRefreshToken) {
        // Legacy mode: coordinatedRefresh shares lock with axiosInterceptor — only ONE fetch fires (CWE-362)
        const { id_token, access_token, refresh_token: newRefreshToken } = await coordinatedRefresh(currentRefreshToken);
        let updatedSession = {
          sessionToken: access_token,
          refreshToken: newRefreshToken,
          idToken: id_token,
        };
        store.dispatch(updateUserActiveSessionDelta(updatedSession));
      } else {
        // Redis mode: no refresh token in frontend, send credentials-only request to extend session
        const { getInviteUserLoginEndPoint } = await import('../configuration');
        const response = await fetch(getInviteUserLoginEndPoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ grantType: 'refresh_token' }),
        });
        if (response.status === 200) {
          const responseData = await response.json();
          if (responseData.session_state) {
            store.dispatch(updateUserActiveSessionDelta({ sessionState: responseData.session_state }));
          }
        }
      }
      resetIdleTimer();
    } catch (error) {
      // On token refresh error, stop all timers and logout
      cancelTokenRefresh();
      stopIdleTimer();
      // Invalidate server session (best-effort — refresh token may already be invalid)
      await invalidateTokenApi(activeSession).catch(() => {});
      store.dispatch(resetApp());
      clearAllCookies();
      clearAuthStorage();
      localStorage.clear();
      if (
        window.location.pathname !== ROUTES.LOGIN &&
        window.location.pathname !== ROUTES.DASHBOARD
      ) {
        sessionStorage.setItem(STORAGE.RURL, window.location.pathname);
      }
      // Pass error via URL param (not sessionStorage) — clears on browser refresh
      window.location.replace(window.location.origin + ROUTES.LOGIN + "?reason=refresh_failed");
    }
  } else {
    // Stop ALL timers and intervals immediately to prevent duplicate triggers
    cancelTokenRefresh();
    stopIdleTimer();

    // Save redirect URL BEFORE clearing (so it survives the clear)
    const redirectUrl =
      window.location.pathname !== ROUTES.LOGIN &&
      window.location.pathname !== ROUTES.DASHBOARD
        ? window.location.pathname
        : null;

    // Reset Redux store BEFORE clearing localStorage so redux-persist cannot
    // write stale persisted data (e.g. sites hierarchy) back after the clear.
    store.dispatch(resetApp());

    // Clear sensitive data
    clearAllCookies();
    sessionStorage.clear();
    localStorage.clear();

    // Restore redirect URL AFTER clearing (if it was set)
    if (redirectUrl) {
      sessionStorage.setItem(STORAGE.RURL, redirectUrl);
    }

    // Start logout API, then navigate
    console.log("[LOGOUT] Starting token invalidation API call...");

    const logoutApiCall = invalidateTokenApi(activeSession)
      .then(() => {
        console.log("[LOGOUT] Token invalidation completed successfully");
        return true;
      })
      .catch((err) => {
        console.error("[LOGOUT] Token invalidation failed (non-critical):", err);
        return false;
      });

    // Timeout fallback: navigate after 3 seconds even if API hasn't responded
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        console.log("[LOGOUT] Timeout reached (3s), navigating anyway");
        resolve(false);
      }, 3000);
    });

    // Wait for whichever completes first: API call or timeout
    Promise.race([logoutApiCall, timeoutPromise]).then(() => {
      console.log("[LOGOUT] Navigating to login page now");
      window.location.replace(window.location.origin + ROUTES.LOGIN);
    });
  }
};
